import {
	Box,
	Button,
	CircularProgress,
	Grid,
	TextField,
	Typography,
	makeStyles,
} from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import "ag-grid-community/styles/ag-theme-material.min.css";
import { ColDef } from "ag-grid-community";
import MesfModal from "../../../../components/modals/modal.mesf";
import { MESApiService } from "../../../../services";

interface Action {
	ActionId: number;
	ActionName: string;
	ActionTypeId: number;
}

interface CacheInvalidation {
	SourceId: number;
	Source: string;
	TargetsCSV: string;
}

interface FormData {
	source: Action | null;
	targets: Action[];
}

const useStyles = makeStyles((theme) => ({
	root: {
		height: "100vh",
		padding: theme.spacing(4),
	},
	header: {
		marginBottom: theme.spacing(4),
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
	},
	gridContainer: {
		height: "75%",
		width: "100%",
	},
	autocomplete: {
		marginTop: theme.spacing(2),
		marginBottom: theme.spacing(2),
	},
}));

const QueryCacheInvalidations: React.FC = () => {
	const classes = useStyles();
	const [rowData, setRowData] = useState<CacheInvalidation[]>([]);
	const [actions, setActions] = useState<Action[]>([]);
	const [modal, setModal] = useState<"add" | "edit" | "">("");
	const [selectedRow, setSelectedRow] = useState<CacheInvalidation | null>(
		null,
	);
	const [notification, setNotification] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const { control, reset, handleSubmit, setValue } = useForm<FormData>({
		defaultValues: {
			source: null,
			targets: [],
		},
	});

	const columnDefs: ColDef[] = [
		{ field: "SourceId", headerName: "Source ID" },
		{ field: "Source", headerName: "Source" },
		{ field: "TargetsCSV", headerName: "Targets" },
		{
			headerName: "Actions",
			cellRenderer: (params: { data: CacheInvalidation }) => (
				<Box>
					<Button
						variant="contained"
						color="primary"
						onClick={() => handleEdit(params.data)}
					>
						Edit
					</Button>
				</Box>
			),
		},
	];

	const fetchData = async () => {
		try {
			const api = new MESApiService();
			const resp = await api.callV2(
				"[SYSTEM].[GetQueryCacheInvalidations]",
				[],
			);

			if (resp.ok && resp.data) {
				setRowData(resp.data.tables[0].rows);
				setActions(resp.data.tables[1].rows);
			} else {
				setNotification("Failed to fetch data");
			}
		} catch (error) {
			setNotification("Failed to fetch data");
			console.error("Fetch error:", error);
		}
	};

	useEffect(() => {
		fetchData();
	}, []);

	const handleAdd = () => {
		reset({
			source: null,
			targets: [],
		});
		setModal("add");
	};

	const handleEdit = (row: CacheInvalidation) => {
		setSelectedRow(row);
		const targetsIds = row.TargetsCSV.split(",").map(Number);
		reset({
			source: actions.find((a) => a.ActionId === row.SourceId) || null,
			targets: actions.filter((a) => targetsIds.includes(a.ActionId)),
		});
		setModal("edit");
	};

	const onSubmit = async (data: FormData) => {
		if (!data.source) return;

		setIsLoading(true);
		try {
			const api = new MESApiService();
			const resp = await api.callV2(
				"[SYSTEM].[UpsertQueryCacheInvalidations]",
				[
					{
						name: "SourceId",
						value: data.source.ActionId,
					},
					{
						name: "TargetsCSV",
						value: data.targets.map((t) => t.ActionId).join(","),
					},
				],
			);

			if (resp.ok) {
				setNotification("Operation successful");
				setModal("");
				fetchData();
			} else {
				setNotification("Operation failed");
			}
		} catch (error) {
			setNotification("Failed to perform operation");
			console.error("Operation error:", error);
		}
		setIsLoading(false);
	};

	return (
		<Box className={classes.root}>
			<Box className={classes.header}>
				<Typography variant="h4" component="h2">
					Query Cache Invalidations
				</Typography>
				<Button variant="contained" color="primary" onClick={handleAdd}>
					Add New
				</Button>
			</Box>

			<Box className={`${classes.gridContainer} ag-theme-material`}>
				<AgGridReact<CacheInvalidation>
					rowData={rowData}
					columnDefs={columnDefs}
					defaultColDef={{ flex: 1 }}
				/>
			</Box>

			<MesfModal
				title={
					modal === "add" ? "Add Cache Invalidation" : "Edit Cache Invalidation"
				}
				open={["add", "edit"].includes(modal)}
				handleClose={() => setModal("")}
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers>
						<Grid container spacing={3}>
							<Grid item xs={12}>
								<Controller
									control={control}
									name={"source"}
									render={(params) => (
										<Autocomplete<Action>
											options={actions}
											getOptionLabel={(option) => `${option.ActionName}`}
											value={params.field.value}
											onChange={(_, value) => params.field.onChange(value)}
											renderInput={(params: any) => (
												<TextField
													{...params}
													label="Select Source Action"
													variant="outlined"
												/>
											)}
											className={classes.autocomplete}
											disabled={modal === "edit"}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12}>
								<Controller
									control={control}
									name={"targets"}
									render={(params) => (
										<Autocomplete<Action, true, true, true>
											multiple
											options={actions.filter(
												(a) =>
													a.ActionId !== control._formValues.source?.ActionId,
											)}
											getOptionLabel={(option) => option.ActionName}
											value={params.field.value}
											onChange={(_, value) => {
												params.field.onChange(value);
											}}
											renderInput={(params: any) => (
												<TextField
													{...params}
													label="Select Target Actions"
													variant="outlined"
												/>
											)}
											className={classes.autocomplete}
										/>
									)}
								/>
							</Grid>
						</Grid>
					</MesfModal.Content>
					<MesfModal.Actions>
						<Grid container spacing={2} justifyContent="flex-end">
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									variant="contained"
									color="default"
									onClick={() => setModal("")}
								>
									Cancel
								</Button>
							</Grid>
							<Grid item md={3} xs={12} style={{ margin: 0 }}>
								<Button
									fullWidth
									startIcon={isLoading && <CircularProgress size="1rem" />}
									disabled={isLoading}
									variant="contained"
									color={"primary"}
									type="submit"
								>
									Save
								</Button>
							</Grid>
						</Grid>
					</MesfModal.Actions>
				</form>
			</MesfModal>
		</Box>
	);
};

export default QueryCacheInvalidations;
